import { useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  HANDOVER_CATEGORIES,
  SIGNOFF_PARTIES,
  addHandoverItem,
  addHandoverSignoff,
  captureHandoverSignoff,
  deleteHandoverItem,
  deleteHandoverSignoff,
  getHandover,
  onWrite,
  seedHandoverChecklist,
  setHandoverDlp,
  setHandoverItemComplete,
  type HandoverItem,
  type HandoverSignoff,
  type HandoverView,
} from '../api.js';
import { formatDate } from '../labels.js';
import { Drawer } from './register-kit.js';
import { Attachments } from './Attachments.js';

/**
 * Handover (HO-01/02/03) — the checklist, the readiness number, the
 * signatures, and the DLP clock.
 *
 * The number is never shown alone (`06`: no score without its reasons): the
 * breakdown under it says what is still in the way, including open snags and
 * NCRs that live on other screens — because a PM promising a handover date
 * needs the whole truth in one place.
 *
 * The client's part is deliberately small: they see everything and they SIGN
 * (`04` gives them `handover: VA`). The capture button follows `sign_handover`;
 * everything else follows the write capabilities.
 */

const CATEGORY_LABELS: Readonly<Record<string, string>> = {
  as_builts: 'As-builts',
  om_manuals: 'O&M manuals',
  warranties: 'Warranties',
  training: 'Training',
  keys_assets: 'Keys & assets',
  other: 'Other',
};

const PARTY_LABELS: Readonly<Record<string, string>> = {
  client: 'Client',
  consultant: 'Consultant',
  main_contractor: 'Main contractor',
  other: 'Other',
};

interface Props {
  readonly projectId: string;
  readonly can: {
    readonly write: boolean;
    readonly edit: boolean;
    readonly remove: boolean;
    readonly sign: boolean;
  };
}

export function Handover({ projectId, can }: Props): JSX.Element {
  const [view, setView] = useState<HandoverView | null>(null);
  /** Not the same as an empty register — see `Drawings.tsx`. */
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState<'item' | 'signoff' | 'dlp' | null>(null);
  const [capturing, setCapturing] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await getHandover(projectId));
      setDenied(false);
    } catch (caught) {
      const refused =
        caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load the handover.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * Read it again whenever anything is written to THIS project.
   *
   * Readiness is counted from OTHER registers — `snags_open` and `ncrs_open`
   * are totals minus what is closed — so this panel goes stale on writes that
   * never touch it. The owner verified SNG-0001 in the register directly above
   * this one; the register refetched itself and this went on saying "there are
   * already 3 open snags" about a list that had two.
   *
   * Same subscription the stage strip uses, and for the same reason: a screen
   * showing something another register can change has to be told when it does.
   */
  useEffect(
    () => onWrite((path) => { if (path.includes(projectId)) void load(); }),
    [projectId, load],
  );

  async function run(work: () => Promise<HandoverView>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setView(await work());
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** Shut whichever form is open, and clear anything it complained about. */
  const closeForm = useCallback(() => {
    setAdding(null);
    setError(null);
  }, []);

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Handover</h3>
      {view && can.write && (
        <>
          {view.items.length === 0 && (
            <button
              type="button"
              className="inline"
              disabled={busy}
              onClick={() => void run(() => seedHandoverChecklist(projectId))}
            >
              Set up the checklist
            </button>
          )}
          <button type="button" className="inline" onClick={() => setAdding('item')}>
            Add item
          </button>
          <button type="button" className="inline" onClick={() => setAdding('signoff')}>
            Expect a sign-off
          </button>
        </>
      )}
      {view && can.edit && (
        <button type="button" className="inline" onClick={() => setAdding('dlp')}>
          {view.dlp_start_date ? 'Move the DLP start' : 'Set the DLP start'}
        </button>
      )}
    </div>
  );

  if (denied) {
    return (
      <>
        {head}
        <p className="hint">Your role does not include this project&rsquo;s handover.</p>
      </>
    );
  }

  if (!view) {
    return (
      <>
        {head}
        {error ? (
          <div className="alert" role="alert">
            <span>{error}</span>
          </div>
        ) : (
          <p aria-live="polite">Loading the handover…</p>
        )}
      </>
    );
  }

  const { readiness } = view;

  return (
    <>
      {head}

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {/* The number, never alone. */}
      {readiness.not_set_up ? (
        <p className="hint">
          <strong>Readiness cannot be measured yet</strong> — the checklist has not been set up.
          {readiness.snags_open + readiness.ncrs_open > 0 && (
            <>
              {' '}
              There {readiness.snags_open + readiness.ncrs_open === 1 ? 'is' : 'are'} already{' '}
              {readiness.snags_open > 0 &&
                `${readiness.snags_open} open snag${readiness.snags_open === 1 ? '' : 's'}`}
              {readiness.snags_open > 0 && readiness.ncrs_open > 0 && ' and '}
              {readiness.ncrs_open > 0 &&
                `${readiness.ncrs_open} open NCR${readiness.ncrs_open === 1 ? '' : 's'}`}{' '}
              waiting to count against it.
            </>
          )}
        </p>
      ) : (
        <div className="handover-readiness">
          <p className="panel-title">
            {readiness.percent}% ready
            <span className="visually-hidden"> for handover</span>
          </p>
          <div
            className="meter"
            role="progressbar"
            aria-valuenow={readiness.percent ?? 0}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Handover readiness"
          >
            <div className="meter-fill" style={{ width: `${readiness.percent ?? 0}%` }} />
          </div>
          <p className="hint">
            {readiness.checklist_done} of {readiness.checklist_total} checklist items done
            {readiness.snags_open > 0 &&
              ` · ${readiness.snags_open} open snag${readiness.snags_open === 1 ? '' : 's'}`}
            {readiness.ncrs_open > 0 &&
              ` · ${readiness.ncrs_open} open NCR${readiness.ncrs_open === 1 ? '' : 's'}`}
            {readiness.snags_open + readiness.ncrs_open > 0 &&
              ' — every open snag and NCR counts against readiness, because each is one more thing between this project and its certificate.'}
          </p>
        </div>
      )}

      {/* The DLP clock, in words. */}
      <p className="hint">
        {view.dlp_start_date ? (
          <>
            <strong>Defects liability:</strong> {view.dlp_months} months from{' '}
            {formatDate(view.dlp_start_date)}.
          </>
        ) : (
          <>
            <strong>The defects liability period has not been started.</strong> Its start date is
            one of the three things the Handover stage cannot complete without.
          </>
        )}
      </p>

      {/*
        The three forms open in a DRAWER rather than above the checklist. They
        used to push the whole list down the page — and the checklist is the
        thing somebody is reading while they decide what to add to it.
      */}
      {adding === 'item' && (
        <Drawer title="Add a handover item" onClose={closeForm}>
          <ItemForm
            busy={busy}
            onCancel={closeForm}
            onSubmit={async (input) => {
              if (await run(() => addHandoverItem(projectId, input))) setAdding(null);
            }}
          />
        </Drawer>
      )}

      {adding === 'signoff' && (
        <Drawer
          title="Expect a sign-off"
          subtitle="Who has to sign, and for what"
          onClose={closeForm}
        >
          <SignoffForm
            busy={busy}
            onCancel={closeForm}
            onSubmit={async (input) => {
              if (await run(() => addHandoverSignoff(projectId, input))) setAdding(null);
            }}
          />
        </Drawer>
      )}

      {adding === 'dlp' && (
        <Drawer
          title={view.dlp_start_date ? 'Move the DLP start' : 'Set the DLP start'}
          onClose={closeForm}
        >
          <DlpForm
            current={view}
            busy={busy}
            onCancel={closeForm}
            onSubmit={async (input) => {
              if (await run(() => setHandoverDlp(projectId, input))) setAdding(null);
            }}
          />
        </Drawer>
      )}

      {/* The checklist */}
      {view.items.length === 0 ? (
        <div className="empty">
          <p>
            <strong>No checklist yet.</strong>
          </p>
          <p>
            {can.write
              ? 'Set it up to get the standard handover pack — as-builts, O&M manuals, warranties, training, keys — then shape it to this job.'
              : 'The handover checklist has not been set up.'}
          </p>
        </div>
      ) : (
        <ul className="task-list">
          {view.items.map((item) => (
            <ItemRow
              key={item.id}
              projectId={projectId}
              item={item}
              busy={busy}
              canEdit={can.edit}
              canRemove={can.remove}
              onToggle={(complete) =>
                void run(() => setHandoverItemComplete(projectId, item.id, complete))
              }
              onDelete={() => void run(() => deleteHandoverItem(projectId, item.id))}
            />
          ))}
        </ul>
      )}

      {/* Sign-offs */}
      <h4 className="sub-head">Sign-offs</h4>
      {view.signoffs.length === 0 ? (
        <p className="hint">
          <strong>Nobody has been asked to sign yet.</strong> The gate will not pass until every
          expected sign-off is captured — and at least one exists. A handover nobody accepted is
          not a handover.
        </p>
      ) : (
        <ul className="task-list">
          {view.signoffs.map((signoff) => (
            <SignoffRow
              key={signoff.id}
              signoff={signoff}
              busy={busy}
              canSign={can.sign}
              canRemove={can.remove}
              capturing={capturing === signoff.id}
              onOpenCapture={() => setCapturing(signoff.id)}
              onCancelCapture={() => setCapturing(null)}
              onCapture={async (input) => {
                if (await run(() => captureHandoverSignoff(projectId, signoff.id, input))) {
                  setCapturing(null);
                }
              }}
              onDelete={() => void run(() => deleteHandoverSignoff(projectId, signoff.id))}
            />
          ))}
        </ul>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function ItemRow({
  projectId,
  item,
  busy,
  canEdit,
  canRemove,
  onToggle,
  onDelete,
}: {
  readonly projectId: string;
  readonly item: HandoverItem;
  readonly busy: boolean;
  readonly canEdit: boolean;
  readonly canRemove: boolean;
  readonly onToggle: (complete: boolean) => void;
  readonly onDelete: () => void;
}): JSX.Element {
  const done = Boolean(item.completed_at);
  return (
    <li className={`task-row${done ? ' is-done' : ''}`}>
      <div className="task-main">
        <span className="task-title">
          {canEdit ? (
            <span className="check">
              <input
                id={`ho-${item.id}`}
                type="checkbox"
                checked={done}
                disabled={busy}
                onChange={(e) => onToggle(e.target.checked)}
              />
              <label htmlFor={`ho-${item.id}`}>{item.title}</label>
            </span>
          ) : (
            // Status in words for the read-only view, never the tick alone.
            <>
              {item.title}
              <span className="tree-code">{' · '}{done ? 'Done' : 'Outstanding'}</span>
            </>
          )}
        </span>
        <span className="task-meta">
          <span>{CATEGORY_LABELS[item.category] ?? item.category}</span>
          {!item.is_mandatory && <span className="tree-code">optional — does not hold the gate</span>}
          {done && item.completed_by_name && (
            <span>
              done by {item.completed_by_name} on {formatDate(item.completed_at)}
            </span>
          )}
          {item.note && <span>{item.note}</span>}
        </span>
        {/*
          The warranty file lives on the row that demands it. Shared by
          default — the handover pack is BUILT to be given to the client.
        */}
        <Attachments
          projectId={projectId}
          entityType="handover_item"
          entityId={item.id}
          can={{ write: canEdit }}
          noun="the file itself"
          category="certificate"
          shareByDefault
        />
      </div>
      {canRemove && (
        <span className="row-actions">
          <button type="button" className="link-button" disabled={busy} onClick={onDelete}>
            Remove<span className="visually-hidden"> {item.title}</span>
          </button>
        </span>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------

function SignoffRow({
  signoff,
  busy,
  canSign,
  canRemove,
  capturing,
  onOpenCapture,
  onCancelCapture,
  onCapture,
  onDelete,
}: {
  readonly signoff: HandoverSignoff;
  readonly busy: boolean;
  readonly canSign: boolean;
  readonly canRemove: boolean;
  readonly capturing: boolean;
  readonly onOpenCapture: () => void;
  readonly onCancelCapture: () => void;
  readonly onCapture: (input: {
    signed_by_name: string;
    signed_on: string;
  }) => void | Promise<void>;
  readonly onDelete: () => void;
}): JSX.Element {
  const signed = Boolean(signoff.signed_on);
  return (
    <li className={`task-row${signed ? ' is-done' : ''}`}>
      <div className="task-main">
        <span className="task-title">
          {signoff.party_name}
          <span className="tree-code">{' · '}{PARTY_LABELS[signoff.party] ?? signoff.party}</span>
        </span>
        <span className="task-meta">
          {signed ? (
            <span>
              Signed by {signoff.signed_by_name} on {formatDate(signoff.signed_on)}
            </span>
          ) : (
            // In words, not by colour: this row is what the gate is waiting on.
            <span className="task-overdue">Not signed yet</span>
          )}
          {signoff.note && <span>{signoff.note}</span>}
        </span>

        {capturing && (
          <CaptureForm busy={busy} onCancel={onCancelCapture} onSubmit={onCapture} />
        )}
      </div>
      <span className="row-actions">
        {!signed && canSign && !capturing && (
          <button type="button" className="link-button" disabled={busy} onClick={onOpenCapture}>
            Capture signature<span className="visually-hidden"> — {signoff.party_name}</span>
          </button>
        )}
        {canRemove && (
          <button type="button" className="link-button" disabled={busy} onClick={onDelete}>
            Remove<span className="visually-hidden"> the sign-off for {signoff.party_name}</span>
          </button>
        )}
      </span>
    </li>
  );
}

// ---------------------------------------------------------------------------

function ItemForm({
  busy,
  onCancel,
  onSubmit,
}: {
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    title: string;
    category: string;
    is_mandatory: boolean;
  }) => void | Promise<void>;
}): JSX.Element {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('other');
  const [mandatory, setMandatory] = useState(true);

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!title.trim()) return;
    void onSubmit({ title: title.trim(), category, is_mandatory: mandatory });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <p className="panel-title">Add a checklist item</p>
      <div className="field">
        <label htmlFor="ho-title">What has to be handed over?</label>
        <input
          id="ho-title"
          value={title}
          maxLength={300}
          required
          disabled={busy}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="ho-category">Category</label>
          <select
            id="ho-category"
            value={category}
            disabled={busy}
            onChange={(e) => setCategory(e.target.value)}
          >
            {HANDOVER_CATEGORIES.map((key) => (
              <option key={key} value={key}>
                {CATEGORY_LABELS[key]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="check">
        <input
          id="ho-mandatory"
          type="checkbox"
          checked={mandatory}
          disabled={busy}
          onChange={(e) => setMandatory(e.target.checked)}
        />
        <label htmlFor="ho-mandatory">
          Must be done before handover — unticked items are tracked but do not hold the gate
        </label>
      </div>
      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !title.trim()}>
          Add
        </button>
        <button type="button" className="secondary inline" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

function SignoffForm({
  busy,
  onCancel,
  onSubmit,
}: {
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: { party: string; party_name: string }) => void | Promise<void>;
}): JSX.Element {
  const [party, setParty] = useState('client');
  const [name, setName] = useState('');

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!name.trim()) return;
    void onSubmit({ party, party_name: name.trim() });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <p className="panel-title">Who has to sign?</p>
      <p className="hint">
        This records that a signature is EXPECTED. The gate then waits for it — capturing the
        actual signature is a separate act, recorded with the name, the date and the paper.
      </p>
      <div className="field-row">
        <div className="field">
          <label htmlFor="so-party">Party</label>
          <select
            id="so-party"
            value={party}
            disabled={busy}
            onChange={(e) => setParty(e.target.value)}
          >
            {SIGNOFF_PARTIES.map((key) => (
              <option key={key} value={key}>
                {PARTY_LABELS[key]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="so-name">Organisation</label>
          <input
            id="so-name"
            value={name}
            maxLength={200}
            required
            disabled={busy}
            placeholder="The company signing off"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
      </div>
      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !name.trim()}>
          Add
        </button>
        <button type="button" className="secondary inline" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

function CaptureForm({
  busy,
  onCancel,
  onSubmit,
}: {
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    signed_by_name: string;
    signed_on: string;
  }) => void | Promise<void>;
}): JSX.Element {
  const [name, setName] = useState('');
  const [date, setDate] = useState('');

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!name.trim() || !date) return;
    void onSubmit({ signed_by_name: name.trim(), signed_on: date });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <p className="panel-title">Capture the signature</p>
      <p className="hint">
        Kept permanently, with your name beside it. A wrong capture is corrected by removing the
        sign-off and capturing again — both acts are recorded.
      </p>
      <div className="field-row">
        <div className="field">
          <label htmlFor="cap-name">Who signed?</label>
          <input
            id="cap-name"
            value={name}
            maxLength={200}
            required
            disabled={busy}
            placeholder="The name on the paper"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="cap-date">On what day?</label>
          <input
            id="cap-date"
            type="date"
            value={date}
            required
            disabled={busy}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
      </div>
      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !name.trim() || !date}>
          Capture
        </button>
        <button type="button" className="secondary inline" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

function DlpForm({
  current,
  busy,
  onCancel,
  onSubmit,
}: {
  readonly current: HandoverView;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: { start_date: string; months: number }) => void | Promise<void>;
}): JSX.Element {
  const [date, setDate] = useState(current.dlp_start_date ?? '');
  const [months, setMonths] = useState(String(current.dlp_months));

  const monthsNumber = Number(months);
  const valid = date !== '' && Number.isInteger(monthsNumber) && monthsNumber >= 1;

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!valid) return;
    void onSubmit({ start_date: date, months: monthsNumber });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <p className="panel-title">The defects liability period</p>
      <p className="hint">
        This starts a clock the contract holds you to — usually the date on the handover
        certificate. Moving it later is allowed and recorded, with both dates kept.
      </p>
      <div className="field-row">
        <div className="field">
          <label htmlFor="dlp-date">Starts on</label>
          <input
            id="dlp-date"
            type="date"
            value={date}
            required
            disabled={busy}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="dlp-months">Months</label>
          <input
            id="dlp-months"
            inputMode="numeric"
            value={months}
            required
            disabled={busy}
            onChange={(e) => setMonths(e.target.value)}
          />
        </div>
      </div>
      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !valid}>
          Save
        </button>
        <button type="button" className="secondary inline" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
